export class MainSectionBuilder {
  constructor() {
    const currentYear = new Date().getFullYear();
    const years: { name: string }[] = [];
    let startYear = 2013;

    while (startYear <= currentYear) {
      const newYear = startYear++;

      years.unshift({ name: newYear.toString() });
    }
  }

  static get TextInputOr() {
    return TextInputOr;
  }

  static get SelectQuantity() {
    return SelectQuantity;
  }

  static get DateFilter() {
    return DateFilter;
  }

  static get MultiSelect() {
    return MultiSelect;
  }

  static getTemplateObject() {
    return {
      main: {
        what: new MultiSelectUseValue('what'),
        status: new Select('status'),
        expiration: new DateFilter('expiration'),
        duration: new SelectQuantity('duration'),
        reservationType: new MultiSelect('reservation_type'),
        when: new DateFilter('when'),
        who: new MultiSelectUseUsername('who'),
        uniqueId: new TextInput('unique_id'),
        sections: new MultiSelectUseId('section_id'),
        rooms: new CheckboxSelect('rooms'),
        areaTotal: new SelectQuantity('area_total'),
        price1sqm: new SelectQuantity('price_1sqm'),
        priceTotal: new SelectQuantity('price_total'),
        storey: new SelectQuantity('storey'),
        whereType: new MultiSelectUseValue('unit_type'),
      },
      lead: {
        leadStatus: new MultiSelect('lead_status'),
        project: new MultiSelectUseId('lead_object'),
        tag: new MultiSelectUseId('lead_tag'),
        dateUpdated: new DateFilter('lead_updated'),
        dateCreated: new DateFilter('lead_created'),
        userUpdated: new MultiSelectUseUsername('lead_updated_by'),
        userCreated: new MultiSelectUseUsername('lead_created_by'),
        hasEmail: new Select('has_email'),
        hasPhone: new Select('has_phone'),
        hasNote: new Select('has_lead_note'),
        note: new TextInputOr('lead_note'),
        unitStatus: new MultiSelect('unit_status'),
        hasTask: new Select('has_lead_task'),
      },
      notes: {
        hasNote: new Select('has_note'),
        quantity: new SelectQuantity('note_quantity'),
        date: new DateFilter('note_created'),
        user: new MultiSelectUseUsername('note_created_by'),
        content: new TextInputAnd('note_content'),
      },
    };
  }
}

class Select {
  value = -1;

  constructor(public name: string) {}

  generateQuery() {
    return this.value === -1 ? null : `${this.name}: "${this.value}"`;
  }
}

class MultiSelect {
  value: { name: string }[] = [];

  constructor(public name: string) {}

  generateQuery() {
    if (!this.value.length) {
      return null;
    }

    let query = '';

    query += `${this.name}: `;

    this.value.forEach((item, i) => {
      query += `"${item.name}"`;

      if (i < this.value.length - 1) {
        query += ', ';
      }
    });

    return query;
  }
}

class MultiSelectUseValue {
  value: { value: string }[] = [];

  constructor(public name: string) {}

  generateQuery(): string | null {
    if (!this.value.length) {
      return null;
    }

    let query = `${this.name}: `;

    this.value.forEach((item, i) => {
      query += `"${item.value}"`;

      if (i < this.value.length - 1) {
        query += ', ';
      }
    });

    return query;
  }
}

class MultiSelectUseId {
  value: { id: number | string }[] = [];

  constructor(
    /**
     * For Sections we write id of section to the query string, not the name
     */
    public name: string,
  ) {}

  generateQuery(): string | null {
    if (!this.value.length) {
      return null;
    }

    let query = `${this.name}: `;

    this.value.forEach((item, i) => {
      query += `${item.id}`;

      if (i < this.value.length - 1) {
        query += ', ';
      }
    });

    return query;
  }
}

class MultiSelectUseUsername {
  value: { username: string }[] = [];

  constructor(public name: string) {}

  generateQuery(): string | null {
    if (!this.value.length) {
      return null;
    }

    let query = `${this.name}: `;

    this.value.forEach((item, i) => {
      query += `"${item.username}"`;

      if (i < this.value.length - 1) {
        query += ', ';
      }
    });

    return query;
  }
}

class DateFilter {
  type: number | string = -1;
  value: number | null = 1;
  point: string = 'days ago';
  month: number = -1;
  year: string = `${new Date().getFullYear()}`;
  years: { name: string }[] = [];
  date?: string | null;

  constructor(public name: string) {
    const currentYear = new Date().getFullYear();
    let startYear = 2013;

    while (startYear <= currentYear) {
      this.years.unshift({ name: (startYear++).toString() });
    }
  }

  generateQuery(): string | null {
    if (this.type === -1) {
      return null;
    }

    let query = this.name;

    const typeStr = String(this.type);

    if (typeStr === 'today' || typeStr === 'yesterday') {
      query += `: "${typeStr}"`;
    } else if (typeStr.indexOf('ago') >= 0) {
      query += `${typeStr}"`;
    } else {
      if (typeStr.indexOf('before') > -1) {
        if (this.point === 'days ago') {
          query += ' > ';
        } else if (this.point === 'days from now') {
          query += ' < ';
        }
      } else if (typeStr.indexOf('after') > -1) {
        if (this.point === 'days ago') {
          query += ' < ';
        } else if (this.point === 'days from now') {
          query += ' > ';
        }
      } else if (typeStr.indexOf('on') > -1) {
        query += ': ';
        this.month = -1;
      } else if (typeStr.indexOf('mo') > -1) {
        query += ': ';
        this.date = null;
      }

      query += '"';

      if (this.date) {
        // we should invert gt/lt signs when we have a date value
        if (query.indexOf('>') > -1) {
          query = query.replace('>', '<');
        } else if (query.indexOf('<') > -1) {
          query = query.replace('<', '>');
        }

        query += this.date;

        this.value = null;
      } else if (this.month === -1) {
        query += `${this.value || 0} ${this.point}`;
      } else {
        query += `${this.year}-${this.month}`;
        this.value = null;
      }

      query += '"';
    }

    return query;
  }
}

class SelectQuantity {
  type: number | string = -1;
  value: number = 1;
  values: { from: number; to: number } = { from: 1, to: 2 };

  constructor(public name: string) {}

  generateQuery(): string | null {
    if (this.type === -1) {
      return null;
    }

    let query = this.name;

    const typeStr = String(this.type);

    switch (typeStr) {
      case 'more':
        query += ` > ${this.value}`;
        break;
      case 'less':
        query += ` < ${this.value}`;
        break;
      case 'exactly':
        query += `: ${this.value}`;
        break;
      case 'between':
        query += ` > ${this.values.from} & ${this.name} < ${this.values.to}`;
        break;
      default:
        break;
    }

    return query;
  }
}

class CheckboxSelect {
  value: string[] = [];

  constructor(public name: string) {}

  generateQuery(): string | null {
    if (!this.value.length) {
      return null;
    }

    let query = `${this.name}: `;

    this.value.forEach((item, i) => {
      query += item;

      if (i < this.value.length - 1) {
        query += ', ';
      }
    });

    return query;
  }

  toggleSelection(item: string): void {
    const index = this.value.indexOf(item);

    if (index >= 0) {
      this.value.splice(index, 1);
    } else {
      this.value.push(item);
    }
  }

  containsSelection(item: string): boolean {
    return this.value.indexOf(item) >= 0;
  }
}

class TextInput {
  value: any = null;

  constructor(public name: string) {}

  generateQuery(): string | null {
    return this.value ? `${this.name}: ${this.value}` : null;
  }
}

class TextInputOr {
  value: string | null = null;
  values: string[] = [];
  enter = false;

  constructor(public name: string) {}

  generateQuery(): string | null {
    if (!this.enter || !this.value) {
      return null;
    }

    let query = `${this.name}: `;

    if (this.value.indexOf(',') === -1) {
      query += `"${this.value}"`;
    } else {
      this.values = this.value.split(',');

      this.values.forEach((item, i) => {
        query += `"${item.trim()}"`;

        if (i < this.values.length - 1) {
          query += ', ';
        }
      });
    }

    return query;
  }
}

class TextInputAnd {
  value: string | null = null;
  values: string[] = [];
  enter = false;

  constructor(public name: string) {}

  generateQuery(): string | null {
    if (!this.enter || !this.value) {
      return null;
    }

    let query = '';

    if (this.value.indexOf(',') === -1) {
      query = `${this.name}: ` + `"${this.value}"`;
    } else {
      this.values = this.value.split(',');

      this.values.forEach((item, i) => {
        query += `${this.name}: `;
        query += `"${item.trim()}"`;

        if (i < this.values.length - 1) {
          query += ' & ';
        }
      });
    }

    return query;
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
class BooleanFilter {
  constructor(
    public name: string,
    public value = false,
    public renderIfFalse = false,
  ) {}

  generateQuery(): string | null {
    if (!this.renderIfFalse && !this.value) {
      return null;
    }

    return `${this.name}: ${this.value}`;
  }
}
